import { memo, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Stage, Layer, Line, Arrow, Rect, Ellipse, Text, Group } from 'react-konva';
import type Konva from 'konva';
import { getStroke } from 'perfect-freehand';
import { getSpineManager, type ViewportState } from '../../lib/spine-manager';
import { useToolStore } from '../../stores/toolStore';
import { useDrawingStore } from '../../stores/drawingStore';
import { usePlaybackStore } from '../../stores/playbackStore';
import { useSceneStore } from '../../stores/sceneStore';
import type { DrawStroke, DrawStrokePoint } from '@spine-workspace/shared';

const RULER_SIZE = 20;

// World ↔ screen (only needed for pointer hit-testing, NOT for shape rendering)
function toWorld(sx: number, sy: number, vp: ViewportState) {
  return { wx: (sx - RULER_SIZE - vp.x) / vp.zoom, wy: (sy - RULER_SIZE - vp.y) / vp.zoom };
}

// Apply a viewport transform to a Konva Layer imperatively (bypasses React reconciliation)
function applyVpToLayer(layer: Konva.Layer, vp: ViewportState) {
  layer.x(RULER_SIZE + vp.x);
  layer.y(RULER_SIZE + vp.y);
  layer.scaleX(vp.zoom);
  layer.scaleY(vp.zoom);
  layer.batchDraw();
}

// ─── Per-type Konva shapes (world-coordinate rendering) ──────────────────────
// Shapes render in WORLD coordinates. The Layer's transform handles screen offset + zoom,
// so these components never need vp. This keeps them stable across pan/zoom.

interface StrokeProps { stroke: DrawStroke; listening?: boolean }

const PenShape = memo(function PenShape({ stroke, listening = false }: StrokeProps) {
  const { points, color, size, isEraser } = stroke;
  if (points.length < 2) return null;
  const worldPts = points.map((p) => [p.x, p.y]);
  const outline = getStroke(worldPts, {
    size: Math.max(0.5, size * 2),
    thinning: 0.5,
    smoothing: 0.5,
    streamline: 0.5,
    last: true,
  });
  if (outline.length < 2) return null;
  return (
    <Line
      points={outline.flat()}
      closed
      fill={isEraser ? 'rgba(0,0,0,1)' : color}
      strokeWidth={0}
      listening={listening}
      {...(isEraser ? { globalCompositeOperation: 'destination-out' as const } : {})}
    />
  );
});

const LineShape = memo(function LineShape({ stroke, listening = false }: StrokeProps) {
  const { points, color, size, isEraser } = stroke;
  if (points.length < 2) return null;
  const p1 = points[0], p2 = points[points.length - 1];
  return (
    <Line
      points={[p1.x, p1.y, p2.x, p2.y]}
      stroke={isEraser ? 'rgba(0,0,0,1)' : color}
      strokeWidth={size}
      lineCap="round"
      listening={listening}
      {...(isEraser ? { globalCompositeOperation: 'destination-out' as const } : {})}
    />
  );
});

const ArrowShape = memo(function ArrowShape({ stroke, listening = false }: StrokeProps) {
  const { points, color, size, isEraser } = stroke;
  if (points.length < 2) return null;
  const p1 = points[0], p2 = points[points.length - 1];
  const c = isEraser ? 'rgba(0,0,0,1)' : color;
  return (
    <Arrow
      points={[p1.x, p1.y, p2.x, p2.y]}
      stroke={c}
      fill={c}
      strokeWidth={size}
      pointerLength={size * 4}
      pointerWidth={size * 2.6}
      listening={listening}
      {...(isEraser ? { globalCompositeOperation: 'destination-out' as const } : {})}
    />
  );
});

const RectShape = memo(function RectShape({ stroke, listening = false }: StrokeProps) {
  const { points, color, size, isEraser, filled } = stroke;
  if (points.length < 2) return null;
  const p1 = points[0], p2 = points[points.length - 1];
  const eraserC = 'rgba(0,0,0,1)';
  return (
    <Rect
      x={Math.min(p1.x, p2.x)} y={Math.min(p1.y, p2.y)}
      width={Math.abs(p2.x - p1.x)} height={Math.abs(p2.y - p1.y)}
      stroke={isEraser ? eraserC : color}
      strokeWidth={size}
      fill={isEraser ? eraserC : (filled ? color + '4D' : undefined)}
      listening={listening}
      {...(isEraser ? { globalCompositeOperation: 'destination-out' as const } : {})}
    />
  );
});

const EllipseShape = memo(function EllipseShape({ stroke, listening = false }: StrokeProps) {
  const { points, color, size, isEraser, filled } = stroke;
  if (points.length < 2) return null;
  const p1 = points[0], p2 = points[points.length - 1];
  const cx = (p1.x + p2.x) / 2, cy = (p1.y + p2.y) / 2;
  const rx = Math.max(0.5, Math.abs(p2.x - p1.x) / 2);
  const ry = Math.max(0.5, Math.abs(p2.y - p1.y) / 2);
  const eraserC = 'rgba(0,0,0,1)';
  return (
    <Ellipse
      x={cx} y={cy} radiusX={rx} radiusY={ry}
      stroke={isEraser ? eraserC : color}
      strokeWidth={size}
      fill={isEraser ? eraserC : (filled ? color + '4D' : undefined)}
      listening={listening}
      {...(isEraser ? { globalCompositeOperation: 'destination-out' as const } : {})}
    />
  );
});

const TextShape = memo(function TextShape({ stroke, listening = false }: StrokeProps) {
  const { points, color, size, text } = stroke;
  if (!text || points.length < 1) return null;
  return (
    <Text
      x={points[0].x} y={points[0].y}
      text={text}
      fontSize={size * 4}
      fontStyle="bold"
      fontFamily="sans-serif"
      fill={color}
      listening={listening}
    />
  );
});

const StrokeShape = memo(function StrokeShape({ stroke, listening = false }: StrokeProps) {
  switch (stroke.type ?? 'pen') {
    case 'pen':     return <PenShape stroke={stroke} listening={listening} />;
    case 'line':    return <LineShape stroke={stroke} listening={listening} />;
    case 'arrow':   return <ArrowShape stroke={stroke} listening={listening} />;
    case 'rect':    return <RectShape stroke={stroke} listening={listening} />;
    case 'ellipse': return <EllipseShape stroke={stroke} listening={listening} />;
    case 'text':    return <TextShape stroke={stroke} listening={listening} />;
    default:        return null;
  }
});

// ─── Selection bounding-box outline (world coordinates) ──────────────────────

const SelectionOutline = memo(function SelectionOutline({ stroke }: { stroke: DrawStroke }) {
  const xs = stroke.points.map((p) => p.x);
  const ys = stroke.points.map((p) => p.y);
  const minX = Math.min(...xs), maxX = Math.max(...xs);
  const minY = Math.min(...ys), maxY = Math.max(...ys);
  const pad = 8;
  return (
    <Rect
      x={minX - pad} y={minY - pad}
      width={Math.max(16, maxX - minX + pad * 2)}
      height={Math.max(16, maxY - minY + pad * 2)}
      stroke="#63b3ed"
      strokeWidth={1.5}
      dash={[5, 3]}
      fill="transparent"
      listening={false}
      cornerRadius={3}
    />
  );
});

// ─── Selectable / draggable wrapper ──────────────────────────────────────────

interface SelectableStrokeProps {
  stroke: DrawStroke;
  isSelected: boolean;
  onSelect: () => void;
  onMoveEnd: (dx: number, dy: number) => void;
}

const SelectableStroke = memo(function SelectableStroke({
  stroke, isSelected, onSelect, onMoveEnd,
}: SelectableStrokeProps) {
  return (
    <Group
      draggable
      onClick={(e) => { e.cancelBubble = true; onSelect(); }}
      onTap={(e) => { e.cancelBubble = true; onSelect(); }}
      onDragEnd={(e) => {
        // Group is inside the scaled Layer, so .x()/.y() are already world-space deltas
        const dx = e.target.x();
        const dy = e.target.y();
        e.target.position({ x: 0, y: 0 });
        onMoveEnd(dx, dy);
      }}
    >
      <StrokeShape stroke={stroke} listening={true} />
      {isSelected && <SelectionOutline stroke={stroke} />}
    </Group>
  );
});

// ─── Component ────────────────────────────────────────────────────────────────

interface Props { readOnly?: boolean }
interface PendingText { sx: number; sy: number; wx: number; wy: number }

export default function DrawOverlay({ readOnly }: Props) {
  const containerRef = useRef<HTMLDivElement>(null);
  const isDrawingRef = useRef(false);
  const startPointRef = useRef<DrawStrokePoint | null>(null);
  const currentPointsRef = useRef<DrawStrokePoint[]>([]);
  const [previewVersion, setPreviewVersion] = useState(0);
  const [pendingText, setPendingText] = useState<PendingText | null>(null);
  const textInputRef = useRef<HTMLInputElement>(null);
  const [stageSize, setStageSize] = useState({ w: 0, h: 0 });

  // vp lives in a ref — viewport changes update the Layer imperatively without React re-renders
  const vpRef = useRef<ViewportState>(getSpineManager().getViewportState());
  const layerRef = useRef<Konva.Layer>(null);

  const activeTool = useToolStore((s) => s.activeTool);
  const isDrawMode = activeTool === 'draw' && !readOnly;

  const { drawings, visible, activeDrawTool, selectedStrokeId } = useDrawingStore();
  const frame = usePlaybackStore((s) => s.currentFrame);
  const sceneId = useSceneStore((s) => s.sceneId);

  const isSelectMode = isDrawMode && activeDrawTool === 'select';

  // Auto-show drawings when entering draw mode
  useEffect(() => {
    if (isDrawMode && !useDrawingStore.getState().visible) {
      useDrawingStore.getState().toggleVisible();
    }
  }, [isDrawMode]);

  // Cancel pending text if tool changes
  useEffect(() => {
    if (!isDrawMode) setPendingText(null);
  }, [isDrawMode]);

  // Deselect stroke when leaving select mode
  useEffect(() => {
    if (!isSelectMode) useDrawingStore.getState().selectStroke(null);
  }, [isSelectMode]);

  // Delete key removes selected stroke in select mode
  useEffect(() => {
    if (!isSelectMode) return;
    function onKeyDown(e: KeyboardEvent) {
      const tag = (e.target as HTMLElement)?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA') return;
      if (e.code === 'Delete' || e.code === 'Backspace') {
        const { selectedStrokeId: id } = useDrawingStore.getState();
        if (id && sceneId) {
          e.preventDefault();
          useDrawingStore.getState().removeStroke(sceneId, frame, id);
        }
      }
    }
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [isSelectMode, sceneId, frame]);

  // Viewport sync — update the Layer transform imperatively, no React state update
  useEffect(() => {
    const unsub = getSpineManager().onViewportChange((newVp) => {
      vpRef.current = newVp;
      if (layerRef.current) applyVpToLayer(layerRef.current, newVp);
    });
    return () => unsub();
  }, []);

  // Apply initial Layer transform once layer is mounted
  useLayoutEffect(() => {
    if (layerRef.current) applyVpToLayer(layerRef.current, vpRef.current);
  });

  // Load frame on change
  useEffect(() => {
    const { sceneId: sid } = useSceneStore.getState();
    if (sid) useDrawingStore.getState().loadFrame(sid, frame);
  }, [frame]);

  // Resize stage to container
  useLayoutEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    function resize() {
      if (!container) return;
      setStageSize({ w: container.clientWidth, h: container.clientHeight });
    }
    resize();
    const ro = new ResizeObserver(resize);
    ro.observe(container);
    return () => ro.disconnect();
  }, []);

  // Auto-focus text input
  useEffect(() => {
    if (pendingText) setTimeout(() => textInputRef.current?.focus(), 0);
  }, [pendingText]);

  function getWorldPoint(e: React.PointerEvent<HTMLDivElement>): DrawStrokePoint {
    const rect = containerRef.current!.getBoundingClientRect();
    const { wx, wy } = toWorld(e.clientX - rect.left, e.clientY - rect.top, vpRef.current);
    return { x: wx, y: wy };
  }

  function handlePointerDown(e: React.PointerEvent<HTMLDivElement>) {
    if (!isDrawMode || isSelectMode) return;
    const { activeDrawTool: adt } = useDrawingStore.getState();

    if (adt === 'text') {
      const rect = containerRef.current!.getBoundingClientRect();
      const sx = e.clientX - rect.left;
      const sy = e.clientY - rect.top;
      const { wx, wy } = toWorld(sx, sy, vpRef.current);
      setPendingText({ sx, sy, wx, wy });
      return;
    }

    e.currentTarget.setPointerCapture(e.pointerId);
    isDrawingRef.current = true;
    const pt = getWorldPoint(e);
    startPointRef.current = pt;
    currentPointsRef.current = [pt];
    setPreviewVersion((v) => v + 1);
  }

  function handlePointerMove(e: React.PointerEvent<HTMLDivElement>) {
    if (!isDrawingRef.current || !isDrawMode) return;
    const { activeDrawTool: adt } = useDrawingStore.getState();
    const pt = getWorldPoint(e);

    if (adt === 'pen') {
      currentPointsRef.current = [...currentPointsRef.current, pt];
    } else {
      currentPointsRef.current = [startPointRef.current!, pt];
    }
    setPreviewVersion((v) => v + 1);
  }

  async function handlePointerUp(_e: React.PointerEvent<HTMLDivElement>) {
    if (!isDrawingRef.current) return;
    isDrawingRef.current = false;
    const points = [...currentPointsRef.current];
    currentPointsRef.current = [];
    startPointRef.current = null;

    const { sceneId: sid } = useSceneStore.getState();
    const f = usePlaybackStore.getState().currentFrame;
    if (sid && points.length >= 2) {
      const commitPromise = useDrawingStore.getState().commitStroke(sid, f, points);
      setPreviewVersion((v) => v + 1);
      await commitPromise;
    } else {
      setPreviewVersion((v) => v + 1);
    }
  }

  async function commitText(value: string) {
    if (!pendingText) return;
    const captured = pendingText;
    setPendingText(null);
    if (!value.trim()) return;
    const { sceneId: sid } = useSceneStore.getState();
    const f = usePlaybackStore.getState().currentFrame;
    if (sid) {
      await useDrawingStore.getState().commitStroke(
        sid, f,
        [{ x: captured.wx, y: captured.wy }],
        { type: 'text', text: value.trim() },
      );
    }
  }

  const cursor = isDrawMode
    ? isSelectMode ? 'default' : activeDrawTool === 'text' ? 'text' : 'crosshair'
    : 'default';

  const frameStrokes = (visible ? drawings[frame] : null) ?? [];

  // Preview stroke built from ref (no stale closure — ref is always current)
  const previewPts = currentPointsRef.current;
  let previewStroke: DrawStroke | null = null;
  if (previewPts.length >= 2) {
    const { brushColor, brushSize, isEraser, activeDrawTool: adt, filled } = useDrawingStore.getState();
    previewStroke = {
      id: '',
      type: adt === 'select' ? 'pen' : adt,
      color: brushColor,
      size: brushSize,
      isEraser,
      points: previewPts,
      filled,
    };
  }
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  void previewVersion; // consumed to trigger re-render

  return (
    <div
      ref={containerRef}
      className="absolute inset-0 z-[18]"
      style={{ pointerEvents: isDrawMode ? 'auto' : 'none', cursor }}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerUp}
      onPointerCancel={handlePointerUp}
    >
      {stageSize.w > 0 && stageSize.h > 0 && (
        <Stage
          width={stageSize.w}
          height={stageSize.h}
          style={{ position: 'absolute', top: 0, left: 0 }}
          listening={isSelectMode}
          onClick={isSelectMode ? () => useDrawingStore.getState().selectStroke(null) : undefined}
        >
          {/* Layer transform is set imperatively via layerRef — no React state for vp */}
          <Layer ref={layerRef} listening={isSelectMode}>
            {frameStrokes.map((stroke) =>
              isSelectMode ? (
                <SelectableStroke
                  key={stroke.id}
                  stroke={stroke}
                  isSelected={selectedStrokeId === stroke.id}
                  onSelect={() => useDrawingStore.getState().selectStroke(stroke.id)}
                  onMoveEnd={(dx, dy) => {
                    const sid = useSceneStore.getState().sceneId;
                    const f = usePlaybackStore.getState().currentFrame;
                    if (sid) useDrawingStore.getState().moveStroke(sid, f, stroke.id, dx, dy);
                  }}
                />
              ) : (
                <StrokeShape key={stroke.id} stroke={stroke} />
              )
            )}
            {previewStroke && <StrokeShape key="preview" stroke={previewStroke} />}
          </Layer>
        </Stage>
      )}

      {/* Inline text input overlay */}
      {pendingText && (
        <input
          ref={textInputRef}
          type="text"
          className="absolute bg-transparent border-none outline-none text-white font-bold"
          style={{
            left: pendingText.sx,
            top: pendingText.sy,
            fontSize: Math.max(12, useDrawingStore.getState().brushSize * 4),
            color: useDrawingStore.getState().brushColor,
            caretColor: useDrawingStore.getState().brushColor,
            minWidth: 120,
          }}
          placeholder="Type here…"
          onKeyDown={(e) => {
            if (e.key === 'Enter') commitText(e.currentTarget.value);
            if (e.key === 'Escape') setPendingText(null);
          }}
          onBlur={(e) => commitText(e.currentTarget.value)}
        />
      )}
    </div>
  );
}
